import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import {
  ChevronLeft, ShoppingBag, Upload, Pencil, Trash2, Plus, X, Loader2, Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const CATEGORIES = [
  { id: 'apparel', label: 'Apparel' },
  { id: 'products', label: 'Products' },
  { id: 'gift', label: 'Gift Cards' },
  { id: 'other', label: 'Other' },
];

async function compressImage(file) {
  if (file.size <= MAX_BYTES) return file;
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const MAX_EDGE = 1600;
  let { width: w, height: h } = img;
  if (Math.max(w, h) > MAX_EDGE) {
    const ratio = MAX_EDGE / Math.max(w, h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);
  for (const q of [0.85, 0.75, 0.65, 0.5]) {
    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', q));
    if (blob && blob.size <= MAX_BYTES) {
      return new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    }
  }
  return file;
}

const emptyForm = () => ({
  id: null,
  name: '',
  price: '',
  category: 'apparel',
  description: '',
  sizes: '',
  in_stock: true,
  sort_order: 0,
  imageFile: null,
  imagePreview: null,
  removeImage: false,
  existingImageUrl: null,
});

const AdminMerch = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/merch/items?include_unavailable=true');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load merch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openNew = () => {
    setForm(emptyForm());
    setEditorOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      price: String(item.price ?? ''),
      category: item.category || 'apparel',
      description: item.description || '',
      sizes: (item.sizes || []).join(', '),
      in_stock: item.in_stock !== false,
      sort_order: item.sort_order || 0,
      imageFile: null,
      imagePreview: null,
      removeImage: false,
      existingImageUrl: item.image_url || null,
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
    setEditorOpen(false);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('JPEG / PNG / WebP only');
      return;
    }
    let final = file;
    if (file.size > MAX_BYTES) {
      const t = toast.loading(`Compressing (${(file.size / 1024 / 1024).toFixed(1)} MB)…`);
      try { final = await compressImage(file); } catch (_) {}
      toast.dismiss(t);
      if (final.size > MAX_BYTES) {
        toast.error('Photo still too large after compression');
        return;
      }
    }
    if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
    setForm((f) => ({ ...f, imageFile: final, imagePreview: URL.createObjectURL(final), removeImage: false }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const priceNum = parseFloat(form.price);
    if (Number.isNaN(priceNum) || priceNum < 0) { toast.error('Enter a valid price'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('price', priceNum.toFixed(2));
      fd.append('category', form.category);
      fd.append('description', form.description || '');
      fd.append('sizes', form.sizes || '');
      fd.append('in_stock', String(form.in_stock));
      fd.append('sort_order', String(parseInt(form.sort_order || 0, 10)));
      if (form.imageFile) fd.append('image', form.imageFile);
      if (form.id && form.removeImage && !form.imageFile) fd.append('remove_image', 'true');

      let res;
      if (form.id) {
        res = await api.put(`/api/merch/items/${form.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await api.post('/api/merch/items', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success(form.id ? 'Item updated' : 'Item added');
      closeEditor();
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      setDeletingId(item.id);
      await api.delete(`/api/merch/items/${item.id}`);
      setItems((prev) => prev.filter(i => i.id !== item.id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const previewImg = form.imagePreview || (form.removeImage ? null : form.existingImageUrl);

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-4 md:p-8" data-testid="admin-merch-page">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          className="mb-6 text-zinc-400 hover:text-white"
          onClick={() => navigate('/admin')}
          data-testid="back-to-dashboard-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-lime-500/10 border border-lime-500/30">
              <ShoppingBag className="w-7 h-7 text-lime-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Merch Manager</h1>
              <p className="text-zinc-400 text-sm mt-1">Add, edit, and remove items from the customer Merch page.</p>
            </div>
          </div>
          <Button
            onClick={openNew}
            className="bg-lime-400 text-black hover:bg-lime-500 font-semibold"
            data-testid="merch-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add item
          </Button>
        </div>

        {/* Reset popup history shortcut */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-purple-500/30 bg-purple-500/5">
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-purple-300">🔄 Re-pop merch for everyone:</span>{' '}
            <span className="text-zinc-400">After adding a new item you want every customer to see at next login, tap here.</span>
          </p>
          <Button
            size="sm"
            variant="outline"
            className="border-purple-500/50 text-purple-200 hover:bg-purple-500/10 flex-shrink-0"
            onClick={async () => {
              if (!window.confirm('Reset the merch popup "seen" state for ALL customers?\n\nEveryone will see the latest in-stock item on their next login.')) return;
              try {
                const r = await api.post('/api/merch/popup/reset-all');
                toast.success(`Reset ${r.data.users_reset} user(s). Popups will re-show on next login.`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Reset failed');
              }
            }}
            data-testid="reset-merch-popup-btn"
          >
            Reset popup history
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-lime-400 mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-10 text-center">
              <ShoppingBag className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-400">No items yet. Tap <b>Add item</b> to add your first one.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item) => (
              <Card key={item.id} className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid={`admin-merch-${item.id}`}>
                <div className="relative h-44 bg-zinc-800 flex items-center justify-center">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700">
                      <ImageIcon className="w-12 h-12" />
                    </div>
                  )}
                  {item.in_stock === false && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                      OUT OF STOCK
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold truncate">{item.name}</p>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">{item.category}</p>
                    </div>
                    <p className="text-lime-400 font-extrabold text-lg">${Number(item.price).toFixed(2)}</p>
                  </div>
                  {item.description && (
                    <p className="text-zinc-400 text-sm mt-2 line-clamp-2">{item.description}</p>
                  )}
                  {item.sizes && item.sizes.length > 0 && (
                    <p className="text-xs text-zinc-500 mt-2">Sizes: {item.sizes.join(', ')}</p>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => openEdit(item)}
                      className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      data-testid={`merch-edit-${item.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleDelete(item)}
                      disabled={deletingId === item.id}
                      className="border-red-500/40 text-red-300 hover:bg-red-500/15"
                      data-testid={`merch-delete-${item.id}`}
                    >
                      {deletingId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Editor */}
        {editorOpen && (
          <div
            onClick={() => !saving && closeEditor()}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
              zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            data-testid="merch-editor-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: 560, width: '100%', maxHeight: '92vh', overflowY: 'auto',
                background: 'linear-gradient(160deg, rgba(30,30,40,0.96), rgba(15,15,15,0.96))',
                border: '1px solid rgba(170,255,0,0.3)', borderRadius: 18, padding: 22,
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">{form.id ? 'Edit item' : 'New item'}</h3>
                <button onClick={closeEditor} aria-label="Close" className="text-zinc-500 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Image */}
              <Label className="text-zinc-300">Photo</Label>
              {previewImg ? (
                <div className="relative rounded-lg overflow-hidden mt-1 mb-2">
                  <img src={previewImg} alt="preview" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', display: 'block', background: '#000' }} />
                  <div className="absolute bottom-2 right-2 flex gap-2">
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="text-xs bg-black/70 text-white px-3 py-1 rounded-full border border-white/15"
                      data-testid="merch-change-image"
                    >
                      Change
                    </button>
                    <button
                      onClick={() => {
                        if (form.imagePreview) URL.revokeObjectURL(form.imagePreview);
                        setForm((f) => ({ ...f, imageFile: null, imagePreview: null, removeImage: true }));
                      }}
                      className="text-xs bg-red-900/80 text-red-200 px-3 py-1 rounded-full border border-red-500/40"
                      data-testid="merch-remove-image"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full mt-1 mb-2 py-8 px-4 rounded-lg border-2 border-dashed border-lime-400/40 bg-lime-400/5 text-zinc-300 hover:bg-lime-400/10 flex flex-col items-center gap-1"
                  data-testid="merch-pick-image"
                >
                  <Upload className="w-7 h-7 text-lime-400" />
                  <span className="font-semibold">Tap to upload photo</span>
                  <span className="text-xs opacity-60">JPEG / PNG / WebP · large photos auto-compressed</span>
                </button>
              )}
              <input
                ref={fileRef} type="file" accept="image/*" onChange={handleFile}
                style={{ display: 'none' }} data-testid="merch-file-input"
              />

              <Label className="text-zinc-300 mt-3 block">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Sauce Sity T-Shirt"
                className="bg-zinc-950 border-zinc-700 text-white"
                data-testid="merch-name-input"
              />

              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-zinc-300">Price (USD) *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      placeholder="25.00"
                      className="bg-zinc-950 border-zinc-700 text-white pl-7"
                      data-testid="merch-price-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Category</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full h-10 rounded-md bg-zinc-950 border border-zinc-700 text-white px-3"
                    data-testid="merch-category-select"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <Label className="text-zinc-300 mt-3 block">Description</Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Premium quality cotton t-shirt with logo"
                className="w-full rounded-md bg-zinc-950 border border-zinc-700 text-white p-2 text-sm"
                data-testid="merch-description-input"
              />

              <Label className="text-zinc-300 mt-3 block">Sizes / variants</Label>
              <Input
                value={form.sizes}
                onChange={(e) => setForm((f) => ({ ...f, sizes: e.target.value }))}
                placeholder="S, M, L, XL, 2XL"
                className="bg-zinc-950 border-zinc-700 text-white"
                data-testid="merch-sizes-input"
              />
              <p className="text-xs text-zinc-500 mt-1">Comma-separated. Leave blank if N/A.</p>

              <div className="flex items-center gap-3 mt-4">
                <input
                  id="in_stock"
                  type="checkbox"
                  checked={form.in_stock}
                  onChange={(e) => setForm((f) => ({ ...f, in_stock: e.target.checked }))}
                  className="w-4 h-4 accent-lime-400"
                  data-testid="merch-stock-checkbox"
                />
                <Label htmlFor="in_stock" className="text-zinc-300 cursor-pointer">In stock — visible to customers</Label>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={closeEditor}
                  disabled={saving}
                  className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  data-testid="merch-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-semibold"
                  data-testid="merch-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add item'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMerch;
